import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import { useTranslation } from "@/shared/i18n";

import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { DatePicker } from "@/shared/ui/date-picker";
import { CurrencyInput } from "@/shared/ui/currency-input";
import { Loader2, ArrowLeft, Plus, Save } from "lucide-react";

import {
  PaymentStatus,
  PAYMENT_STATUS_LABELS,
  PendingPaymentDocument,
  PaymentCreatorBankAccount,
} from "../utils/types";
import {
  usePayment,
  usePaymentCreators,
  useCreatorBankAccounts,
  useCreatePayment,
  useUpdatePayment,
  useCreatePaymentDocument,
} from "../hooks/payment.hooks";
import { PaymentDocumentsSection, CreatorBankAccountDialog } from "../components";
import { Alert, AlertDescription, AlertTitle } from "@/shared/ui/alert";
import { paymentFormSchema, type PaymentFormValues } from "../model/payment.schema";

const formatDateForApi = (date: Date | undefined) => {
  if (!date) return undefined;
  try {
    return format(date, "yyyy-MM-dd");
  } catch {
    return undefined;
  }
};

export function PaymentForm() {
  const { t } = useTranslation('payments');
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [pendingDocuments, setPendingDocuments] = useState<PendingPaymentDocument[]>([]);
  const [uploadingDocuments, setUploadingDocuments] = useState(false);
  const [isBankAccountDialogOpen, setIsBankAccountDialogOpen] = useState(false);
  const [hasFormBeenReset, setHasFormBeenReset] = useState(false);

  const { payment, isLoading: loadingPayment } = usePayment(id);
  const { creators, isLoading: loadingCreators } = usePaymentCreators();
  const creatorOptions = useMemo(() => creators.map((creator) => ({ value: creator.id, label: creator.name })), [creators]);

  const form = useForm<PaymentFormValues>({
    resolver: zodResolver(paymentFormSchema),
    defaultValues: {
      creatorId: "",
      creatorBankAccountId: "",
      status: PaymentStatus.PENDING,
      value: 0,
      paymentDate: undefined,
      referencePaymentDate: undefined,
    },
  });

  const creatorId = form.watch("creatorId");
  const {
    bankAccounts,
    isLoading: loadingBankAccounts,
    refetch: refetchBankAccounts,
  } = useCreatorBankAccounts(creatorId);

  const { createPayment, isLoading: creatingPayment } = useCreatePayment();
  const { updatePayment, isLoading: updatingPayment } = useUpdatePayment();
  const { createPaymentDocument } = useCreatePaymentDocument();

  const isSubmitting = creatingPayment || updatingPayment;
  const isLocked = Boolean(isEdit && payment?.status === PaymentStatus.PAID);

  // Reset do formulário quando os dados estiverem prontos
  useEffect(() => {
    if (!isEdit || !payment || hasFormBeenReset) return;
    if (loadingCreators) return; // Aguardar creators carregarem

    const paymentDate = payment.paymentDate ? new Date(payment.paymentDate) : undefined;
    const referenceDate = payment.referencePaymentDate ? new Date(payment.referencePaymentDate) : undefined;

    // Extrair creatorId e creatorBankAccountId dos dados aninhados ou diretos
    const extractedCreatorId = payment.creatorId ?? payment.creator?.id ?? "";
    const extractedBankAccountId = payment.creatorBankAccountId ?? payment.bankAccount?.id ?? "";

    // Se há creators carregados, verificar se o creator do pagamento existe na lista
    if (creators.length > 0 && extractedCreatorId) {
      const creatorExists = creators.some((creator) => creator.id === extractedCreatorId);
      if (!creatorExists) {
        return; // Aguardar até que o creator esteja disponível na lista
      }
    }

    // Preencher o formulário mesmo se a lista de creators ainda estiver vazia
    // O creatorId será definido e o Select mostrará quando os creators carregarem
    form.reset({
      creatorId: extractedCreatorId,
      creatorBankAccountId: extractedBankAccountId,
      status: payment.status ?? PaymentStatus.PENDING,
      value: payment.value ?? 0,
      paymentDate: paymentDate,
      referencePaymentDate: referenceDate,
    });
    
    setHasFormBeenReset(true);
  }, [payment, isEdit, form, creators, loadingCreators, hasFormBeenReset]);

  // Resetar o flag quando sair do modo de edição
  useEffect(() => {
    if (!isEdit) {
      setHasFormBeenReset(false);
    }
  }, [isEdit]);

  const handlePendingDocumentCreate = (document: PendingPaymentDocument) => {
    setPendingDocuments((prev) => [...prev, document]);
  };

  const handlePendingDocumentDelete = (documentId: string) => {
    setPendingDocuments((prev) => prev.filter((document) => document.id !== documentId));
  };

  const handleNavigateBack = () => {
    navigate("/payments");
  };

  const handleAccountSelected = (account: PaymentCreatorBankAccount) => {
    form.setValue("creatorBankAccountId", account.id, { shouldDirty: true });
    setIsBankAccountDialogOpen(false);
    void refetchBankAccounts();
  };

  const selectedBankAccounts = useMemo(() => bankAccounts ?? [], [bankAccounts]);

  useEffect(() => {
    if (!creatorId) {
      form.setValue("creatorBankAccountId", "");
      return;
    }

    const currentValue = form.getValues("creatorBankAccountId");
    if (selectedBankAccounts.length === 0) {
      if (currentValue) {
        form.setValue("creatorBankAccountId", "");
      }
      return;
    }

    if (!currentValue && selectedBankAccounts.length === 1) {
      form.setValue("creatorBankAccountId", selectedBankAccounts[0].id);
      return;
    }

    const exists = currentValue
      ? selectedBankAccounts.some((account) => account.id === currentValue)
      : false;
    if (currentValue && !exists) {
      form.setValue("creatorBankAccountId", "");
    }
  }, [creatorId, selectedBankAccounts, form]);

  const handleSubmit = async (values: PaymentFormValues) => {
    if (isLocked) return;
    const payload = {
      creatorId: values.creatorId,
      creatorBankAccountId: values.creatorBankAccountId,
      status: values.status,
      value: values.value,
      paymentDate: formatDateForApi(values.paymentDate) ?? "",
      referencePaymentDate: formatDateForApi(values.referencePaymentDate) ?? null,
    };

    if (!payload.paymentDate) {
      form.setError("paymentDate", {
        type: "manual",
        message: "Informe a data do pagamento",
      });
      return;
    }

    if (isEdit && id) {
      await updatePayment({
        id,
        ...payload,
      });

      // Criar documentos pendentes após atualizar o pagamento
      if (pendingDocuments.length > 0) {
        setUploadingDocuments(true);
        try {
          for (const document of pendingDocuments) {
            await createPaymentDocument({
              paymentId: id,
              documentType: document.documentType,
              description: document.description,
              file: document.file,
            });
          }
        } finally {
          setUploadingDocuments(false);
        }
      }

      navigate("/payments");
      return;
    }

    try {
      const created = await createPayment(payload);
      if (!created?.id) {
        navigate("/payments");
        return;
      }

      if (pendingDocuments.length > 0) {
        setUploadingDocuments(true);
        try {
          for (const document of pendingDocuments) {
            await createPaymentDocument({
              paymentId: created.id,
              documentType: document.documentType,
              description: document.description,
              file: document.file,
            });
          }
        } finally {
          setUploadingDocuments(false);
        }
      }

      navigate("/payments");
    } catch (error) {
      console.error("Erro ao salvar pagamento:", error);
    }
  };

  if (isEdit && loadingPayment) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={handleNavigateBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Carregando pagamento...</h1>
            <p className="text-muted-foreground">Buscando dados cadastrados</p>
          </div>
        </div>
        <Card>
          <CardContent className="flex h-48 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={handleNavigateBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isEdit ? t('editPaymentTitle') : t('newPaymentTitle')}
          </h1>
          <p className="text-muted-foreground">
            {isEdit
              ? t('updatePaymentDescription')
              : t('newPaymentDescription')}
          </p>
        </div>
      </div>

      <Card className="bg-white/[0.03] border-white/[0.06]">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">{t('paymentInfo')}</CardTitle>
          <CardDescription>{t('paymentInfoDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          {isLocked && (
            <Alert variant="default" className="mb-6 border-amber-500/50 bg-amber-500/10 text-amber-600 dark:text-amber-400">
              <AlertTitle>{t('paymentLocked')}</AlertTitle>
              <AlertDescription>
                {t('paymentLockedDescription')}
              </AlertDescription>
            </Alert>
          )}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="creatorId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('creator')}</FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={loadingCreators || isSubmitting || isLocked}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={loadingCreators ? t('loadingCreators') : t('selectCreator')} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {creatorOptions.length === 0 && !loadingCreators ? (
                            <div className="px-2 py-1.5 text-sm text-muted-foreground">{t('noCreatorAvailable')}</div>
                          ) : (
                            creatorOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <FormDescription>{t('creatorReceivesPayment')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="creatorBankAccountId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('bankAccount')}</FormLabel>
                      <div className="flex gap-2">
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                          disabled={!creatorId || loadingBankAccounts || isSubmitting || isLocked}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder={creatorId ? t('selectAccount') : t('selectCreatorFirst')} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {selectedBankAccounts.length === 0 ? (
                              <div className="px-3 py-2 text-sm text-muted-foreground">
                                {t('noAccountAvailable')}
                              </div>
                            ) : (
                              selectedBankAccounts.map((account) => (
                                <SelectItem key={account.id} value={account.id}>
                                  {account.label ?? `${account.bank ?? "Banco"} - ${account.account}`}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setIsBankAccountDialogOpen(true)}
                          disabled={!creatorId || isLocked}
                        >
                          {t('manage')}
                        </Button>
                      </div>
                      <FormDescription>{t('accountForPayment')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('status')}</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange} disabled={isSubmitting || isLocked}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={t('selectStatus')} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Object.values(PaymentStatus).map((status) => (
                            <SelectItem key={status} value={status}>
                              {PAYMENT_STATUS_LABELS[status]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>{t('paymentStatusDescription')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="value"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('value')}</FormLabel>
                      <FormControl>
                        <CurrencyInput
                          currency="BRL"
                          value={field.value}
                          onValueChange={(num) => field.onChange(num ?? 0)}
                          placeholder="R$ 0,00"
                          disabled={isSubmitting || isLocked}
                        />
                      </FormControl>
                      <FormDescription>{t('valueDescription')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="paymentDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{t('paymentDateLabel')}</FormLabel>
                      <DatePicker
                        date={field.value}
                        onSelect={field.onChange}
                        placeholder={t('selectDate')}
                        disabled={isSubmitting || isLocked}
                      />
                      <FormDescription>{t('paymentDateDescription')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="referencePaymentDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{t('referenceOptional')}</FormLabel>
                      <DatePicker
                        date={field.value}
                        onSelect={field.onChange}
                        placeholder={t('selectReferenceDate')}
                        disabled={isSubmitting || isLocked}
                      />
                      <FormDescription>{t('referenceDescription')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <PaymentDocumentsSection
                paymentId={isEdit ? id : undefined}
                pendingDocuments={pendingDocuments}
                onPendingDocumentCreate={handlePendingDocumentCreate}
                onPendingDocumentDelete={handlePendingDocumentDelete}
                isProcessing={uploadingDocuments || isSubmitting}
                isLocked={isLocked}
              />

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={handleNavigateBack} disabled={isSubmitting}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={isSubmitting || isLocked}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      {isEdit ? <Save className="mr-2 h-4 w-4" /> : <Plus className="mr-2 h-4 w-4" />}
                      {isEdit ? "Salvar alterações" : "Cadastrar"}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      <CreatorBankAccountDialog
        open={isBankAccountDialogOpen}
        onOpenChange={setIsBankAccountDialogOpen}
        creatorId={creatorId || undefined}
        onAccountSelected={handleAccountSelected}
      />
    </div>
  );
}
